import type {
    ClientOptionModel,
    ClientOptionResponse,
    CreateUserParams,
    ProjectOptionModel,
    ProjectOptionResponse,
    SaveAssignmentParams,
    UpdateUserParams,
    UserAssignmentModel,
    UserAssignmentResponse,
    UserModel,
    UserResponse
} from '@/domain/models/user.js';
import {
    ClientOptionModel as ClientOptionModelClass,
    ProjectOptionModel as ProjectOptionModelClass,
    UserAssignmentModel as UserAssignmentModelClass,
    UserModel as UserModelClass
} from '@/domain/models/user.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import type { UserRepository } from '@/domain/repositories/user-repository.js';

export class UserRepositoryImpl implements UserRepository {
    constructor(private readonly httpClient: HttpClient) {}

    public async findAll(params?: {
        search?: string;
        profile?: string;
        status?: string;
        page?: number;
        limit?: number;
    }): Promise<{ data: UserModel[]; total: number; page: number; totalPages: number }> {
        const qs = new URLSearchParams();
        if (params?.search) {
            qs.set('search', params.search);
        }
        if (params?.profile) {
            qs.set('profile', params.profile);
        }
        if (params?.status) {
            qs.set('status', params.status);
        }
        if (params?.page) {
            qs.set('page', String(params.page));
        }
        if (params?.limit) {
            qs.set('limit', String(params.limit));
        }
        const response = await this.httpClient.request<{ data: UserResponse[]; total: number; page: number; totalPages: number }>({
            url: `/api/users?${qs}`,
            method: 'GET'
        });
        return { data: UserModelClass.withFromResponseList(response.body.data), total: response.body.total, page: response.body.page, totalPages: response.body.totalPages };
    }

    public async findById(id: string): Promise<UserModel> {
        const response = await this.httpClient.request<UserResponse>({ url: `/api/users/${encodeURIComponent(id)}`, method: 'GET' });
        return UserModelClass.withFromResponse(response.body);
    }

    public async create(params: CreateUserParams): Promise<UserModel> {
        const response = await this.httpClient.request<UserResponse>({ url: '/api/users', method: 'POST', body: params });
        return UserModelClass.withFromResponse(response.body);
    }

    public async update(id: string, params: UpdateUserParams): Promise<UserModel> {
        const response = await this.httpClient.request<UserResponse>({ url: `/api/users/${encodeURIComponent(id)}`, method: 'PUT', body: params });
        return UserModelClass.withFromResponse(response.body);
    }

    public async block(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/users/${encodeURIComponent(id)}/block`, method: 'PATCH' });
    }

    public async unblock(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/users/${encodeURIComponent(id)}/unblock`, method: 'PATCH' });
    }

    public async resetPassword(id: string): Promise<void> {
        await this.httpClient.request({ url: `/api/users/${encodeURIComponent(id)}/reset-password`, method: 'POST' });
    }

    public async findAssignments(userId: string): Promise<UserAssignmentModel[]> {
        const response = await this.httpClient.request<UserAssignmentResponse[]>({ url: `/api/users/${encodeURIComponent(userId)}/assignments`, method: 'GET' });
        return UserAssignmentModelClass.withFromResponseList(response.body);
    }

    public async saveAssignments(userId: string, assignments: SaveAssignmentParams[]): Promise<UserAssignmentModel[]> {
        const response = await this.httpClient.request<UserAssignmentResponse[]>({
            url: `/api/users/${encodeURIComponent(userId)}/assignments`,
            method: 'PUT',
            body: { assignments }
        });
        return UserAssignmentModelClass.withFromResponseList(response.body);
    }

    public async findClientOptions(): Promise<ClientOptionModel[]> {
        const response = await this.httpClient.request<ClientOptionResponse[]>({ url: '/api/users/options/clients', method: 'GET' });
        return ClientOptionModelClass.withFromResponseList(response.body);
    }

    public async findProjectOptions(clientId: string): Promise<ProjectOptionModel[]> {
        const response = await this.httpClient.request<ProjectOptionResponse[]>({
            url: `/api/users/options/projects?clientId=${encodeURIComponent(clientId)}`,
            method: 'GET'
        });
        return ProjectOptionModelClass.withFromResponseList(response.body);
    }
}
